import TypographyParagraph from '../../typography/TypographyParagraph'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { useRef } from 'react'
import Image from '#app/components/content/Image'
import Audio from '#app/components/content/Audio'
import { MediaAttestationVideo } from '#app/components/content/Video'
import { EyeIcon, FileIcon, ArrowDownIcon, AudioIcon } from '../../icons'
import { Button } from '../../buttons'
import { downloadFile, getArweaveGatewayUrl } from '#app/utils/misc'

export default function MediaUploadTemplate({
	mediaUpload,
}: {
	mediaUpload: any
}) {
	const tooltipCopyTitle = useRef<TooltipRefProps>(null)
	const tooltipCopyDescription = useRef<TooltipRefProps>(null)
	const tooltipCopyFileType = useRef<TooltipRefProps>(null)
	const tooltipCopyFileSize = useRef<TooltipRefProps>(null)
	const tooltipCopySignature = useRef<TooltipRefProps>(null)

	return (
		<>
			<div
				id="title"
				className="flex flex-row border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(mediaUpload.properties.title)
					tooltipCopyTitle.current?.open({
						anchorSelect: '#title',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyTitle.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyTitle} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Title:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all"
				>
					{mediaUpload.properties.title}
				</TypographyParagraph>
			</div>
			<div
				id="description"
				className="flex flex-row border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(mediaUpload.properties.description)
					tooltipCopyDescription.current?.open({
						anchorSelect: '#description',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyDescription.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyDescription} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Description:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{mediaUpload.properties.description}
				</TypographyParagraph>
			</div>
			{/* MEDIA */}
			<div
				id="file"
				className="flex flex-row border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
			>
				{/* <Tooltip imperativeModeOnly ref={tooltipCopyFile} /> */}
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					{mediaUpload.properties.fileType}:
				</TypographyParagraph>
				{mediaUpload.properties.fileType === 'video' ? (
					<div className="mx-auto flex h-auto w-full max-w-[644px] justify-center">
						<MediaAttestationVideo
							videoUrl={getArweaveGatewayUrl(
								mediaUpload.properties.arweaveTxId,
							)}
							title={
								mediaUpload.properties.title
									? mediaUpload.properties.title
									: mediaUpload.properties.fileName
							}
						/>
					</div>
				) : null}
				{mediaUpload.properties.fileType === 'image' ? (
					<div className="mx-auto flex h-auto w-full max-w-[644px] justify-center">
						<Image
							className="h-auto w-full"
							imageUrl={getArweaveGatewayUrl(
								mediaUpload.properties.arweaveTxId,
							)}
							imageAlt={mediaUpload.properties.fileName}
							containerClassName="flex items-center justify-center w-full"
						/>
					</div>
				) : null}
				{mediaUpload.properties.fileType === 'audio' ? (
					<div className="mx-auto flex h-auto w-full max-w-[644px] flex-col items-center text-textPrimaryLight dark:text-textPrimaryDark">
						<AudioIcon />
						<TypographyParagraph size="medium" additionalClasses="mt-4">
							{mediaUpload.properties.fileName}
						</TypographyParagraph>
						<Audio
							className="mt-4 h-12 w-full"
							containerClassName="w-full"
							audioURL={getArweaveGatewayUrl(
								mediaUpload.properties.arweaveTxId,
							)}
						/>
					</div>
				) : null}
				{mediaUpload.properties.fileType === 'pdf' ? (
					<div className="flex w-full flex-col items-center bg-grey150 py-8 text-textPrimaryLight dark:bg-grey950 dark:text-textPrimaryDark">
						<FileIcon />
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:textPrimaryDark mt-4"
						>
							{mediaUpload.properties.fileName}
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textScondaryLight dark:textScondaryDark mb-4"
						>
							{mediaUpload.properties.pageCount} Pages •{' '}
							{mediaUpload.properties.fileSize}
						</TypographyParagraph>
						<div className="flex justify-center gap-x-2">
							<Button
								variant="primary"
								size="small"
								leftIcon={<EyeIcon />}
								onClick={() => {
									window.open(
										getArweaveGatewayUrl(mediaUpload.properties.arweaveTxId),
										'_blank',
									)
								}}
							>
								View PDF
							</Button>
							<Button
								variant="secondary"
								size="small"
								leftIcon={<ArrowDownIcon />}
								onClick={() => {
									downloadFile({
										url: getArweaveGatewayUrl(
											mediaUpload.properties.arweaveTxId,
										),
										fileName: mediaUpload.properties.fileName,
									})
								}}
							>
								Download
							</Button>
						</div>
					</div>
				) : null}
			</div>
			<div className="flex flex-col sm:flex-row">
				<div
					id="fileType"
					className="flex flex-row justify-start border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(mediaUpload.properties.fileType)
						tooltipCopyFileType.current?.open({
							anchorSelect: '#fileType',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyFileType.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyFileType} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						FileType:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-4"
					>
						{mediaUpload.properties.fileType === 'pdf'
							? 'pdf'
							: `${mediaUpload.properties.fileType}/${mediaUpload.properties.fileExtension}`}
					</TypographyParagraph>
				</div>
				<div
					id="fileSize"
					className="flex flex-row justify-start border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(mediaUpload.properties.fileSize)
						tooltipCopyFileSize.current?.open({
							anchorSelect: '#fileSize',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyFileSize.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyFileSize} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						File Size:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-4"
					>
						{mediaUpload.properties.fileSize}
					</TypographyParagraph>
				</div>
			</div>
			<div
				id="signature"
				className="flex flex-row p-4 hover:bg-uiSecondaryLight dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(mediaUpload.signature)
					tooltipCopySignature.current?.open({
						anchorSelect: '#signature',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopySignature.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopySignature} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Signature:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all"
				>
					{mediaUpload.signature}
				</TypographyParagraph>
			</div>
		</>
	)
}
